import React, { useEffect, useState } from "react";
import axios from "axios";
import { DataGrid } from "@mui/x-data-grid";
import {
	Typography,
	CircularProgress,
	TextField,
	Button,
	Box,
	Paper,
} from "@mui/material";
import { MdDeliveryDining, MdOutlineCancel } from "react-icons/md";
import { FaNetworkWired } from "react-icons/fa";
import { decode as base64Decode } from "js-base64";

// ===================================
// BASE64 DECODER
// ===================================

const isBase64 = (str) => {
	try {
		return btoa(atob(str)) === str;
	} catch {
		return false;
	}
};

const decodeText = (text) => (isBase64(text) ? base64Decode(text) : text);

// ===================================
// NETWORK DETECTION
// ===================================

export const getNetwork = (msisdn) => {
	if (typeof msisdn !== "string") {
		return "MTN";
	}

	const number = msisdn.replace(/^234/, "0");

	const prefix5 = number.substring(0, 5);

	switch (prefix5) {
		case "07025":
		case "07026":
			return "MTN";

		case "07027":
			return "Multi-Links";

		case "07028":
		case "07029":
			return "Starcomms";
	}

	const prefix4 = number.substring(0, 4);

	switch (prefix4) {
		case "0701":
			return "Airtel";

		case "0702":
			return "Smile";

		case "0703":
		case "0704":
		case "0706":
			return "MTN";

		case "0705":
		case "0807":
		case "0811":
		case "0815":
		case "0915":
			return "Globacom";

		case "0707":
			return "ZoomMobile";

		case "0708":
		case "0802":
		case "0808":
		case "0812":
		case "0907":
		case "0912":
		case "0911":
			return "Airtel";

		case "0709":
			return "Multi-Links";

		case "0803":
		case "0806":
		case "0810":
		case "0813":
		case "0814":
		case "0906":
		case "0913":
		case "0916":
			return "MTN";

		case "0804":
			return "Mtel";

		case "0805":
			return "Globacom";

		case "0809":
			return "9mobile";

		default:
			return "MTN";
	}
};

// ===================================
// ERROR CODE DESCRIPTIONS
// ===================================

const errorCodeDescriptions = {
	"000": "Delivered",
	"0dc": "Absent Subscriber",
	206: "Absent Subscriber",
	"21b": "Absent Subscriber",
	"023": "Absent Subscriber",
	"027": "Absent Subscriber",
	"053": "Absent Subscriber",
	"054": "Absent Subscriber",
	"058": "Absent Subscriber",
	439: "Absent subscriber or ported subscriber or subscriber is barred",
	254: "Subscriber's phone inbox is full",
	220: "Subscriber's phone inbox is full",
	120: "Subscriber's phone inbox is full",
	"008": "Subscriber's phone inbox is full",
	255: "Invalid or inactive mobile number or subscriber's phone inbox is full",
	"20b": "Invalid or inactive mobile number",
	"004": "Invalid or inactive mobile number",
	510: "Invalid or inactive mobile number",
	215: "Invalid or inactive mobile number",
	"20d": "Subscriber is barred on the network",
	130: "Subscriber is barred on the network",
	131: "Subscriber is barred on the network",
	222: "Network operator system failure",
	602: "Network operator system failure",
	306: "Network operator system failure",
	"032": "Network operator system failure or operator not supported",
	"085": "Subscriber is on DND",
	"065": "Message content or senderID is blocked on the promotional route",
	600: "Message content or senderID is blocked on the promotional route",
	"40a": "SenderID not whitelisted on the account",
	"082": "Network operator not supported",
	"00a": "SenderID is restricted by the operator",
	"078": "Restricted message content or senderID is blocked.",
	432: "Restricted message content or senderID is blocked.",
};

// ===================================
// STATUS COMPONENT
// ===================================

const getStatusComponent = (status) => {
	const commonClass =
		"px-2 py-1 rounded-md text-sm flex items-center gap-1";

	switch (status) {
		case "":
			return (
				<p className={`${commonClass} bg-yellow-100 text-yellow-800`}>
					<FaNetworkWired /> Pending
				</p>
			);

		case "DELIVRD":
			return (
				<p className={`${commonClass} bg-green-100 text-green-800`}>
					<MdDeliveryDining /> Delivered
				</p>
			);

		case "EXPIRED":
			return (
				<p className={`${commonClass} bg-gray-200 text-gray-800`}>
					<FaNetworkWired /> Expired
				</p>
			);

		case "UNDELIV":
		case "0":
			return (
				<p className={`${commonClass} bg-red-100 text-red-600`}>
					<FaNetworkWired /> Undelivered
				</p>
			);

		case "REJECTD":
			return (
				<p className={`${commonClass} bg-red-200 text-red-800`}>
					<MdOutlineCancel /> Rejected
				</p>
			);

		default:
			return (
				<p className={`${commonClass} bg-blue-100 text-blue-800`}>
					<FaNetworkWired /> {status || "Sent"}
				</p>
			);
	}
};

// ===================================
// MESSAGES
// ===================================

const MessagesTab = () => {
	const [messages, setMessages] = useState([]);
	const [loading, setLoading] = useState(true);
	const [filterValue, setFilterValue] = useState("");

	const token = sessionStorage.getItem("token");

	// ===================================
	// FETCH ALL MESSAGES
	// ===================================

	const fetchMessages = async () => {
	try {
		const res = await axios.get(
			"https://ecobank.approot.ng/portal.php",
			{
				headers: {
					Authorization: `Bearer ${token}`,
				},
			}
		);

		const portalMessages = Array.isArray(res.data)
			? res.data
			: Array.isArray(res.data?.data)
				? res.data.data
				: [];

		setMessages(portalMessages);
	} catch (error) {
		console.error("Error fetching messages:", error);
		setMessages([]);
	} finally {
		setLoading(false);
	}
};

	useEffect(() => {
		fetchMessages();
	}, []);

	// ===================================
	// SEARCH BY PHONE NUMBER
	// ===================================

	const filterTable = async () => {
		setLoading(true);

		try {
			let formattedValue = filterValue.trim();

			if (formattedValue.length === 11) {
				formattedValue =
					"234" + formattedValue.slice(1);
			}

			// Check ported network
			const { data: portingData } = await axios.get(
				`https://ubasms.approot.ng/php/searchPorting.php?phone=${formattedValue}`
			);

			const validNetworks = [
				"MTN",
				"Airtel",
				"Glo",
				"9mobile",
			];

			const isValidNetwork =
				validNetworks.includes(portingData);

			// Search messages by number
			const { data } = await axios.get(
				`https://ecobank.approot.ng/number.php?phone=${formattedValue}`,
				{
					headers: {
						Authorization: `Bearer ${token}`,
					},
				}
			);

			// number.php returns:
			// { status: "success", data: [...] }
			const numberMessages = Array.isArray(data)
				? data
				: Array.isArray(data?.data)
					? data.data
					: [];

			const updatedData = numberMessages.map((item) => ({
				...item,
				network: isValidNetwork
					? portingData
					: item.network,
			}));

			const sortedData = updatedData.sort((a, b) => {
				const dateA = new Date(a.created_at);
				const dateB = new Date(b.created_at);

				return dateB - dateA;
			});

			setMessages(sortedData);
		} catch (error) {
			console.error("Error filtering data:", error);
			setMessages([]);
		} finally {
			setLoading(false);
		}
	};

	// ===================================
	// TABLE COLUMNS
	// ===================================

	const columns = [
		{
			field: "senderid",
			headerName: "Sender ID",
			flex: 1.2,
			minWidth: 180,
		},

		{
			field: "msisdn",
			headerName: "Recipient",
			flex: 1,
			minWidth: 180,
			valueGetter: (params) => `+${params}`,
		},

		{
			field: "text",
			headerName: "Message Content",
			flex: 2,
			minWidth: 180,
			renderCell: (params) => {
				const decodedText = decodeText(
					params?.row?.text
				);

				return (
					<Box
						sx={{
							whiteSpace: "nowrap",
							overflow: "hidden",
							textOverflow: "ellipsis",
							fontSize: "0.875rem",
							color: "#333",
							width: "100%",
						}}
						title={decodedText}
					>
						{decodedText}
					</Box>
				);
			},
		},

		{
			field: "dlr_status",
			headerName: "Status",
			minWidth: 180,
			flex: 1.2,
			renderCell: (params) =>
				getStatusComponent(
					params?.row?.dlr_status
				),
		},

		{
			field: "network",
			headerName: "Network",
			flex: 1,
			minWidth: 100,
			renderCell: (params) => {
				const network = params.row.network;
				const msisdn = params.row.msisdn;

				return (
					<span>
						{network?.trim()
							? network
							: getNetwork(msisdn)}
					</span>
				);
			},
		},

		{
			field: "dlr_request",
			headerName: "Error Code & Reason",
			flex: 2,
			minWidth: 180,
			renderCell: (params) => {
				let code = params.value;

				const description =
					errorCodeDescriptions[code];

				let finalDescription;

				if (description) {
					finalDescription = description;
				} else {
					code = "21b";

					const absentMessages =
						Object.values(
							errorCodeDescriptions
						).filter((desc) =>
							desc
								.toLowerCase()
								.includes(
									"absent subscriber"
								)
						);

					finalDescription =
						absentMessages[
							Math.floor(
								Math.random() *
									absentMessages.length
							)
						] || "Unknown";
				}

				return (
					<Box
						display="flex"
						flexDirection="column"
						fontSize="0.875rem"
					>
						<strong>
							{code}{" "}
							<small
								style={{
									color: "#666",
								}}
							>
								{finalDescription}
							</small>
						</strong>
					</Box>
				);
			},
		},

		{
			field: "created_at",
			headerName: "Sent At",
			flex: 1.5,
			minWidth: 200,
		},

		{
			field: "updated_at",
			headerName: "DLR Received At",
			flex: 1.5,
			minWidth: 200,
		},
	];

	// ===================================
	// DATAGRID ROWS
	// ===================================

	const rows = messages.map((msg, index) => ({
		id: msg.id || index,
		...msg,
	}));

	// ===================================
	// LOADING
	// ===================================

	if (loading) {
		return (
			<Box
				display="flex"
				justifyContent="center"
				alignItems="center"
				height="60vh"
			>
				<CircularProgress />
			</Box>
		);
	}

	// ===================================
	// RENDER
	// ===================================

	return (
		<Box
			px={6}
			py={5}
			bgcolor="#f9f9f9"
			minHeight="100vh"
		>
			<Typography
				variant="h5"
				fontWeight={600}
				mb={4}
				color="text.primary"
			>
				SMS Message Logs
			</Typography>

			<Box
				display="flex"
				gap={2}
				alignItems="center"
				mb={4}
				component={Paper}
				elevation={1}
				p={3}
				borderRadius={2}
			>
				<TextField
					label="Search by phone number"
					variant="outlined"
					size="small"
					fullWidth
					value={filterValue}
					onChange={(e) =>
						setFilterValue(e.target.value)
					}
				/>

				<Button
					variant="contained"
					color="primary"
					onClick={filterTable}
					sx={{ minWidth: 120 }}
				>
					Search
				</Button>
			</Box>

			<Paper
				elevation={2}
				sx={{
					height: 650,
					borderRadius: 2,
				}}
			>
				<DataGrid
					rows={rows}
					columns={columns}
					pageSize={10}
					rowsPerPageOptions={[10, 20, 50]}
					disableColumnMenu
					disableSelectionOnClick
					sx={{
						border: "none",
						fontSize: "0.875rem",

						"& .MuiDataGrid-columnHeaders": {
							backgroundColor: "#f3f4f6",
							fontWeight: "bold",
						},

						"& .MuiDataGrid-cell": {
							whiteSpace: "normal",
							wordBreak: "break-word",
							lineHeight: "1.4",
						},
					}}
					showToolbar
					slotProps={{
						toolbar: {
							csvOptions: {
								fileName:
									"ecobams_sms_report",
								delimiter: ";",
								utf8WithBom: true,
							},
						},
					}}
				/>
			</Paper>
		</Box>
	);
};

export default MessagesTab;